import React from 'react';
import { Box, Typography, Skeleton, Chip } from '@mui/material';
import { BarChart3 } from 'lucide-react';
import type { Message, ChartSpec, SourceCitation } from '../types/chat.types';
import ChartRenderer from './ChartRenderer';
import SqlResultsTable from './SqlResultsTable';
import HealthTrendsPanel from './HealthTrendsPanel';
import FlightDataPanel from './FlightDataPanel';
import SourceCitations from './SourceCitations';
import ReportCard from './ReportCard';

interface VisualizationPanelProps {
  messages: Message[];
  loading: boolean;
  compact?: boolean;
  onExpandFullscreen?: () => void;
}

const VisualizationPanel: React.FC<VisualizationPanelProps> = ({
  messages,
  loading,
  compact = false,
  onExpandFullscreen,
}) => {
  // Gather visualization data from most recent assistant messages (newest first)
  const assistantMessages = messages
    .map((m, i) => ({ message: m, index: i }))
    .filter((item) => item.message.type === 'assistant' && item.message.queryResult)
    .reverse();

  // In compact mode, only show data from the most recent message
  const relevantMessages = compact ? assistantMessages.slice(0, 1) : assistantMessages;

  // Collect all chart specs, health trends, flight data, etc.
  const allChartSpecs: ChartSpec[] = [];
  let latestHealthTrends: Record<string, unknown> | undefined;
  let latestFlightData: Record<string, unknown> | undefined;
  let latestFlightChartSpecs: ChartSpec[] | undefined;
  let latestSources: SourceCitation[] | undefined;
  let latestReport: Record<string, unknown> | undefined;
  let latestSqlResults: Record<string, unknown>[] | undefined;
  let latestSqlQuery: string | undefined;

  for (const { message } of relevantMessages) {
    const qr = message.queryResult;
    if (!qr) continue;

    if (qr.chart_specs?.length) {
      allChartSpecs.push(...qr.chart_specs);
    }

    if (qr.health_trends && !latestHealthTrends) {
      latestHealthTrends = qr.health_trends;
    }

    if (qr.flight_data && !latestFlightData) {
      latestFlightData = qr.flight_data;
      // Collect flight-specific chart specs by source tag
      latestFlightChartSpecs = qr.chart_specs?.filter((c) => c.source === 'flight_data');
    }

    if (qr.sources?.length && !latestSources) {
      latestSources = qr.sources;
    }

    if (qr.generated_report && !latestReport) {
      latestReport = qr.generated_report;
    }

    if (qr.sql_query && qr.results?.length && !latestSqlResults) {
      latestSqlResults = qr.results;
      latestSqlQuery = qr.sql_query;
    }
  }

  // Deduplicate chart specs: remove any that match health/flight panels
  // (those panels render their own charts)
  const standAloneCharts = allChartSpecs.filter((c) => {
    if (latestHealthTrends && c.source === 'health_trends') return false;
    if (latestFlightData && c.source === 'flight_data') return false;
    return true;
  });

  const hasContent =
    standAloneCharts.length > 0 ||
    latestHealthTrends ||
    latestFlightData ||
    latestSources?.length ||
    latestReport ||
    (latestSqlResults?.length && latestSqlQuery);

  // Compact mode: limited height with expand option
  if (compact) {
    if (!hasContent) return null;

    let totalItems = 0;
    if (standAloneCharts.length) totalItems += standAloneCharts.length;
    if (latestHealthTrends) totalItems++;
    if (latestFlightData) totalItems++;
    if (latestSqlResults?.length) totalItems++;

    return (
      <Box
        sx={{
          maxHeight: 250,
          overflowY: 'auto',
          borderTop: 1,
          borderColor: 'divider',
          p: 1.5,
          bgcolor: 'grey.50',
        }}
      >
        {/* Show max 2 chart items in compact mode */}
        {standAloneCharts.slice(0, 2).map((spec, i) => (
          <ChartRenderer key={`chart-${i}`} spec={spec} height={180} />
        ))}

        {latestHealthTrends && !standAloneCharts.length && (
          <HealthTrendsPanel healthTrends={latestHealthTrends} compact />
        )}

        {latestFlightData && !standAloneCharts.length && !latestHealthTrends && (
          <FlightDataPanel
            flightData={latestFlightData}
            chartSpecs={latestFlightChartSpecs}
            compact
          />
        )}

        {latestSqlResults?.length && latestSqlQuery && !standAloneCharts.length && (
          <SqlResultsTable results={latestSqlResults} compact />
        )}

        {totalItems > 2 && onExpandFullscreen && (
          <Chip
            label={`+${totalItems - 2} more — expand to fullscreen`}
            size="small"
            onClick={onExpandFullscreen}
            sx={{ cursor: 'pointer', mt: 0.5 }}
            variant="outlined"
          />
        )}
      </Box>
    );
  }

  // Full visualization panel (fullscreen mode)
  return (
    <Box
      sx={{
        flex: 1,
        overflowY: 'auto',
        p: 2,
        bgcolor: 'grey.50',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <BarChart3 size={18} />
        <Typography variant="subtitle1" fontWeight="bold">
          Visualizations
        </Typography>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1 }} />
        </Box>
      )}

      {!loading && !hasContent && (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 200,
          }}
        >
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Charts and data will appear here as you ask questions
          </Typography>
        </Box>
      )}

      {!loading && hasContent && (
        <>
          {/* Generated report */}
          {latestReport && <ReportCard report={latestReport} />}

          {/* Health trends with built-in charts */}
          {latestHealthTrends && <HealthTrendsPanel healthTrends={latestHealthTrends} />}

          {/* Flight data with stats table and charts */}
          {latestFlightData && (
            <FlightDataPanel flightData={latestFlightData} chartSpecs={latestFlightChartSpecs} />
          )}

          {/* Stand-alone chart specs (SQL-generated, LLM-generated) */}
          {standAloneCharts.map((spec, i) => (
            <ChartRenderer key={`chart-${i}`} spec={spec} height={300} />
          ))}

          {/* SQL result table */}
          {latestSqlResults?.length && latestSqlQuery ? (
            <SqlResultsTable results={latestSqlResults} />
          ) : null}

          {/* Source citations */}
          {latestSources?.length ? <SourceCitations sources={latestSources} /> : null}
        </>
      )}
    </Box>
  );
};

export default VisualizationPanel;
